import React, { useState } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

const IndustryBreakdown = () => {
  const [expandedId, setExpandedId] = useState(null);

  const industryData = [
    {
      id: 1,
      industry: 'Business & Financial Services',
      mckinsey2023: 40,
      mckinsey2025: 65,
      challengerCuts2025: 'Heavy (administrative support)',
      occupations: ['Business & Financial Operations', 'Office & Administrative Support'],
      avgRiskScore: 33,
      trend: 'collapsing',
      color: 'red',
      analysis: 'AI now handles financial analysis, forecasting, compliance documentation, and routine reporting. Entry through mid-level analyst roles being eliminated rapidly.',
      keyChanges: [
        'AI agents handle complex financial modeling',
        'Automated compliance and regulatory reporting',
        'Natural language query of financial databases',
        'Real-time risk assessment and forecasting'
      ],
      timeline: '18-36 months for major displacement'
    },
    {
      id: 2,
      industry: 'Technology & Software',
      mckinsey2023: 35,
      mckinsey2025: 60,
      challengerCuts2025: '141,159 YTD (bifurcating)',
      occupations: ['Computer & Mathematical'],
      avgRiskScore: 55,
      trend: 'bifurcating',
      color: 'orange',
      analysis: 'Sharp split: entry-level developers and QA highly vulnerable, while AI infrastructure and ML engineering roles grow. GitHub Copilot writing 40-60% of code at top companies.',
      keyChanges: [
        'AI writes boilerplate and routine code',
        'Automated testing and QA',
        'Code review and documentation via AI',
        'But AI infrastructure roles exploding'
      ],
      timeline: '12-24 months for junior roles, growth in senior/infrastructure'
    },
    {
      id: 3,
      industry: 'Retail & E-commerce',
      mckinsey2023: 50,
      mckinsey2025: 70,
      challengerCuts2025: '88,664 YTD (145% increase)',
      occupations: ['Sales & Related'],
      avgRiskScore: 35,
      trend: 'declining',
      color: 'red',
      analysis: 'E-commerce automation, AI sales assistants, and cashierless stores eliminating traditional retail jobs. Only high-touch luxury and complex B2B sales protected.',
      keyChanges: [
        'AI customer service (chatbots, voice)',
        'Cashierless checkout systems',
        'Automated inventory and stocking',
        'E-commerce replacing physical retail'
      ],
      timeline: '18-36 months for most retail positions'
    },
    {
      id: 4,
      industry: 'Healthcare',
      mckinsey2023: 15,
      mckinsey2025: 25,
      challengerCuts2025: 'Minimal (protected)',
      occupations: ['Healthcare Practitioners', 'Healthcare Support'],
      avgRiskScore: 75,
      trend: 'stable',
      color: 'green',
      analysis: 'AI augments diagnosis and administrative work but physical care, patient relationships, and complex medical decisions remain human. Regulatory protection strong.',
      keyChanges: [
        'AI-assisted diagnostics (imaging, pathology)',
        'Automated scheduling and records',
        'Clinical decision support tools',
        'But human care irreplaceable'
      ],
      timeline: '5-10+ years, mostly augmentation not replacement'
    },
    {
      id: 5,
      industry: 'Manufacturing & Production',
      mckinsey2023: 60,
      mckinsey2025: 75,
      challengerCuts2025: 'Ongoing (40-year decline)',
      occupations: ['Production Occupations'],
      avgRiskScore: 35,
      trend: 'declining',
      color: 'red',
      analysis: 'Robotics and AI accelerating 40-year automation trend. Assembly line work, machine operation, and quality inspection increasingly automated. Skilled trades more protected.',
      keyChanges: [
        'Intelligent robots in unstructured environments',
        'AI quality inspection and defect detection',
        'Automated material handling',
        'Lights-out manufacturing expanding'
      ],
      timeline: '24-48 months for routine assembly, skilled trades protected'
    },
    {
      id: 6,
      industry: 'Transportation & Logistics',
      mckinsey2023: 45,
      mckinsey2025: 70,
      challengerCuts2025: '47,878 warehouse cuts in Oct 2025 alone',
      occupations: ['Transportation & Material Moving'],
      avgRiskScore: 40,
      trend: 'declining',
      color: 'orange',
      analysis: 'Warehouse automation happening NOW (Amazon, others). Autonomous vehicles advancing. Last-mile delivery more protected than warehousing and long-haul.',
      keyChanges: [
        'Warehouse robots replacing pickers/packers',
        'Autonomous forklifts and material handling',
        'Self-driving truck pilots expanding',
        'Drone delivery in testing'
      ],
      timeline: '18-36 months for warehousing, 36-60 months for trucking'
    },
    {
      id: 7,
      industry: 'Legal Services',
      mckinsey2023: 30,
      mckinsey2025: 55,
      challengerCuts2025: 'Moderate (paralegals, junior associates)',
      occupations: ['Legal Occupations'],
      avgRiskScore: 45,
      trend: 'bifurcating',
      color: 'orange',
      analysis: 'Document review, legal research, contract drafting increasingly automated. Trial lawyers and complex negotiators protected. Sharp split between routine and strategic work.',
      keyChanges: [
        'AI legal research and case analysis',
        'Automated contract review and drafting',
        'Discovery and document analysis',
        'But courtroom advocacy remains human'
      ],
      timeline: '18-30 months for paralegals/doc review, strategic roles protected'
    },
    {
      id: 8,
      industry: 'Skilled Trades (Construction, Maintenance)',
      mckinsey2023: 20,
      mckinsey2025: 30,
      challengerCuts2025: 'Minimal (growing demand)',
      occupations: ['Construction & Extraction', 'Installation, Maintenance & Repair'],
      avgRiskScore: 73,
      trend: 'stable',
      color: 'green',
      analysis: 'Physical work in unstructured environments resists automation. AI infrastructure buildout creates NEW demand for HVAC, electrical, data center technicians. Strong protection.',
      keyChanges: [
        'AI diagnostic tools aid repairs',
        'Drones for inspection and surveying',
        'But hands-on work remains human',
        'Data center boom creates demand'
      ],
      timeline: '5-10+ years, augmentation not replacement'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      red: 'border-red-500 bg-red-50',
      orange: 'border-orange-500 bg-orange-50',
      yellow: 'border-yellow-500 bg-yellow-50',
      green: 'border-green-500 bg-green-50'
    };
    return colors[color] || colors.yellow;
  };

  const getTrendIcon = (trend) => {
    switch(trend) {
      case 'declining':
      case 'collapsing':
        return <TrendingDown className="h-6 w-6 text-red-600" />;
      case 'stable':
        return <TrendingUp className="h-6 w-6 text-green-600" />;
      case 'bifurcating':
        return <AlertTriangle className="h-6 w-6 text-orange-600" />;
      default:
        return <TrendingUp className="h-6 w-6 text-yellow-600" />;
    }
  };

  const getChange = (old, current) => {
    const change = ((current - old) / old * 100).toFixed(0);
    return `+${change}%`;
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-4">Industry-by-Industry Breakdown</h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          How McKinsey's automation assessments changed from 2023 to 2025 across major industries, 
          validated with actual October 2025 Challenger layoff data.
        </p>
      </div>

      <div className="grid gap-6">
        {industryData.map((industry) => (
          <div key={industry.id} className={`bg-white rounded-xl shadow-lg border-l-8 ${getColorClasses(industry.color)} overflow-hidden`}>
            {/* Header - Always Visible */}
            <div 
              className="p-6 cursor-pointer hover:bg-gray-50"
              onClick={() => setExpandedId(expandedId === industry.id ? null : industry.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-2xl font-bold">{industry.industry}</h3>
                    {getTrendIcon(industry.trend)}
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-4 gap-4 mb-4">
                    <div className="bg-blue-50 rounded-lg p-3">
                      <div className="text-xs text-gray-600 mb-1">McKinsey 2023</div>
                      <div className="text-2xl font-bold text-blue-700">{industry.mckinsey2023}%</div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3">
                      <div className="text-xs text-gray-600 mb-1">McKinsey 2025</div>
                      <div className="text-2xl font-bold text-red-700">{industry.mckinsey2025}%</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3">
                      <div className="text-xs text-gray-600 mb-1">Change</div>
                      <div className="text-xl font-bold text-purple-700">
                        {getChange(industry.mckinsey2023, industry.mckinsey2025)}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-600 mb-1">Risk Score</div>
                      <div className="text-2xl font-bold text-gray-700">{industry.avgRiskScore}/100</div>
                    </div>
                  </div>

                  <div className="text-sm text-gray-600">
                    <strong>Oct 2025 Challenger:</strong> {industry.challengerCuts2025}
                  </div>
                </div>

                <button className="text-gray-400 hover:text-gray-600 ml-4">
                  {expandedId === industry.id ? <ChevronUp className="h-6 w-6" /> : <ChevronDown className="h-6 w-6" />}
                </button>
              </div>
            </div>

            {/* Expanded Details */}
            {expandedId === industry.id && (
              <div className="border-t border-gray-200 bg-gray-50 p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Analysis */}
                  <div>
                    <h4 className="font-bold mb-3 text-lg">Analysis</h4>
                    <p className="text-gray-700 mb-4">{industry.analysis}</p>
                    
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Timeline</div>
                      <div className="text-gray-600">{industry.timeline}</div>
                    </div>
                  </div>

                  {/* Key Changes */}
                  <div>
                    <h4 className="font-bold mb-3 text-lg">What Changed (2023 → 2025)</h4>
                    <ul className="space-y-2">
                      {industry.keyChanges.map((change, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="text-purple-600 font-bold mt-1">→</span>
                          <span className="text-gray-700">{change}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="text-sm font-semibold text-blue-900 mb-2">Related Occupations</div>
                      <div className="text-sm text-blue-800">
                        {industry.occupations.join(', ')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary Box */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-8 text-white mt-8">
        <h3 className="text-2xl font-bold mb-4 text-center">Key Takeaways</h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-4xl font-bold mb-2">+90%</div>
            <div className="text-purple-100">Average acceleration across industries</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold mb-2">2 years</div>
            <div className="text-purple-100">From "30% by 2030" to "57% NOW"</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold mb-2">4</div>
            <div className="text-purple-100">Major tech breakthroughs driving change</div>
          </div>
        </div>
      </div>

      {/* Update Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-900">
          <strong>Data Sources:</strong> McKinsey Global Institute "Generative AI and Future of Work" (July 2023), 
          "Agents, Robots, and Us" (November 2025), Challenger Gray & Christmas October 2025 Report, 
          PivotIntel Occupation Risk Tracker. Industry data updated quarterly.
        </p>
      </div>
    </div>
  );
};

export default IndustryBreakdown;